import { useEffect, useRef, useState } from "react";
import type { SelectedImage } from "./InsuranceCardUpload";

interface Props {
  title: string;
  onCapture: (img: SelectedImage) => void;
  onClose: () => void;
}

/**
 * In-page camera for computers. A file input's `capture` attribute only opens the camera on
 * phones and tablets; desktop browsers ignore it and show a file browser, so "Take a photo"
 * uses the webcam here instead (the old portal did the same).
 */
export function WebcamCapture({ title, onCapture, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [shot, setShot] = useState<string | null>(null);

  /** Point the <video> at the stream; runs on first stream and whenever the video remounts (Retake). */
  const attachStream = (video: HTMLVideoElement | null) => {
    videoRef.current = video;
    if (!video || !streamRef.current || video.srcObject === streamRef.current) return;
    video.srcObject = streamRef.current;
    void video.play().catch(() => undefined);
  };

  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser can't use a camera. Choose a photo, file, or PDF instead.");
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        attachStream(videoRef.current);
      })
      .catch((e: unknown) => {
        const name = e instanceof DOMException ? e.name : "";
        setError(
          name === "NotAllowedError"
            ? "Camera access was blocked. Allow the camera in your browser, or choose a photo, file, or PDF instead."
            : "No camera was found. Choose a photo, file, or PDF instead."
        );
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL("image/jpeg", 0.9));
  };

  const usePhoto = () => {
    if (!shot) return;
    onCapture({ dataUrl: shot, base64Raw: shot.split(",")[1] ?? "", extension: "jpg" });
  };

  const buttonClass =
    "min-h-11 flex-1 rounded-md px-4 py-2 text-[14px] font-bold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white p-4 shadow-lg"
        style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="mb-3 text-[15px] font-extrabold text-healthsource-dark">{title}</p>
        {error ? (
          <p className="py-6 text-center text-[14px] font-semibold text-red-600">{error}</p>
        ) : (
          <div className="relative overflow-hidden rounded-lg bg-black">
            {shot ? (
              <img src={shot} alt="Captured card" className="w-full" />
            ) : (
              <video
                ref={attachStream}
                playsInline
                muted
                onLoadedData={() => setReady(true)}
                className="w-full"
              />
            )}
            {!shot && !ready && (
              <span className="absolute inset-0 flex items-center justify-center text-[13px] text-white">
                Starting camera…
              </span>
            )}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className={`${buttonClass} border border-slate-200 bg-white text-healthsource-gray`}
          >
            Cancel
          </button>
          {!error && shot && (
            <button
              type="button"
              onClick={() => setShot(null)}
              className={`${buttonClass} border border-healthsource-navy bg-white text-healthsource-navy`}
            >
              Retake
            </button>
          )}
          {!error && (
            <button
              type="button"
              onClick={shot ? usePhoto : capture}
              disabled={!shot && !ready}
              className={`${buttonClass} bg-healthsource-red text-white`}
            >
              {shot ? "Use photo" : "Capture"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
